<script>
    import CheckBoxAreaForInvoicesComponent from "./CheckBoxAreaForInvoicesComponent.svelte";
    import { onMount } from 'svelte';
    import CheckBoxAreForClassesComponent from "./CheckBoxAreForClassesComponent.svelte";
    import CompletedPaymentsList from './CompletedPaymentsListComponent.svelte';
    import NavBarReusable from "./NavbarReusable.svelte";
    import TeacherInvoicesPage from "./TeacherInvoicesPage.svelte";
    import { push } from 'svelte-spa-router';
    import { getApiBase } from './apiBase.js';
    import { buildInvoiceClassWindow, retainVisibleSelections } from './invoiceClassWindow.js';
    import {
        InvoiceApiError,
        clearInvoiceAuthentication,
        ensureActiveInvoiceSession,
        requestInvoiceJson,
    } from './invoicePageApi.js';
    const apiBase = getApiBase();
    let selectedMonths = [];

    let invoiceSum = 0; // Only set after invoice is generated
    let paymentLink = '';
    let isFetchingPaymentLink = false;

    let selectedClasses = [];

    let invoiceData = null;
    let visibleOlderMonthCount = 0;
    let visibleFutureMonthCount = 0;
    $: classWindow = buildInvoiceClassWindow(invoiceData?.classes || [], {
        visibleOlderMonthCount,
        visibleFutureMonthCount,
    });
    let errorForInvoiceData = null;
    let isLoadingInvoiceData = true; // Add loading state
    let isUserTeacher = null;
    let isCheckingUserType = true;
    let backLinkUrl = "/login-greeting"; // Always back to login greeting
    let isRecalculatingMyBalance = false;

    function showOneMorePastMonth() {
        visibleOlderMonthCount += 1;
    }

    function showOneLessPastMonth() {
        const nextMonthCount = Math.max(0, visibleOlderMonthCount - 1);
        const smallerWindow = buildInvoiceClassWindow(invoiceData?.classes || [], {
            visibleOlderMonthCount: nextMonthCount,
            visibleFutureMonthCount,
        });
        selectedClasses = retainVisibleSelections(
            selectedClasses,
            smallerWindow.visibleClasses,
        );
        visibleOlderMonthCount = nextMonthCount;
    }

    function showOneMoreFutureMonth() {
        visibleFutureMonthCount += 1;
    }

    function showOneLessFutureMonth() {
        const nextMonthCount = Math.max(0, visibleFutureMonthCount - 1);
        const smallerWindow = buildInvoiceClassWindow(invoiceData?.classes || [], {
            visibleOlderMonthCount,
            visibleFutureMonthCount: nextMonthCount,
        });
        selectedClasses = retainVisibleSelections(
            selectedClasses,
            smallerWindow.visibleClasses,
        );
        visibleFutureMonthCount = nextMonthCount;
    }

    function redirectToInvoiceLogin() {
        clearInvoiceAuthentication(localStorage);
        window.location.href = '/studentplayground/svelte-try/?redirect=invoice#/about';
    }

    function activeToken() {
        return ensureActiveInvoiceSession(
            localStorage.getItem('jwtToken'),
            localStorage.getItem('jwtExpiry'),
        );
    }

    // Helper: calculate invoice sum as sum of amount_due for selected classes
    function calculateInvoiceSum() {
        if (!invoiceData || !invoiceData.classes) {
            console.log('🔄 STANDALONE - calculateInvoiceSum: No invoice data or classes');
            return 0;
        }
        const sum = selectedClasses.reduce((sum, classId) => {
            const cls = invoiceData.classes.find(c => c.id === classId);
            // Ensure amount_due is always treated as a float number
            const amount = cls ? parseFloat(cls.amount_due) || 0.0 : 0.0;
            console.log(`🔄 STANDALONE - Class ${classId}: amount_due=${cls?.amount_due}, converted=${amount}`);
            return sum + amount;
        }, 0.0);
        console.log(`🔄 STANDALONE - Total invoice sum calculated: ${sum} for classes:`, selectedClasses);
        return Math.round(sum * 100) / 100; // Round to 2 decimal places to avoid floating point errors
    }

    // Update invoiceSum whenever selectedClasses changes
    $: {
        console.log('🔄 STANDALONE - Reactive update triggered. Selected classes:', selectedClasses);
        invoiceSum = calculateInvoiceSum();
        console.log('🔄 STANDALONE - Updated invoiceSum to:', invoiceSum);
    }

    function getPaidFromUntil() {
        if (!invoiceData?.classes || selectedClasses.length === 0) return { paid_from: null, paid_until: null };

        const selectedClassObjects = invoiceData.classes.filter(c => selectedClasses.includes(c.id));
        const sortedByTime = selectedClassObjects.slice().sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
        const paid_from = sortedByTime[0]?.start_time;
        const paid_until = sortedByTime[sortedByTime.length - 1]?.start_time;
        return { paid_from, paid_until };
    }



    // Fetch payment link and redirect immediately (one-click payment)
    async function fetchPaymentLinkAndRedirect() {
        let token;
        try {
            token = activeToken();
        } catch (error) {
            if (error instanceof InvoiceApiError && error.authenticationRequired) {
                redirectToInvoiceLogin();
                return;
            }
            throw error;
        }
        console.log('🚀 STANDALONE - fetchPaymentLinkAndRedirect check:', { 
            hasToken: !!token, 
            invoiceSum, 
            selectedClassesLength: selectedClasses.length 
        });
        
        if (!token || selectedClasses.length === 0) {
            console.error('🚀 STANDALONE - Payment link validation failed:', {
                hasToken: !!token,
                selectedClassesLength: selectedClasses.length
            });
            alert("Prašome pasirinkti nors vieną pamoką, kurią norėtumėte apmokėti.");
            return;
        }

        if (!invoiceSum || invoiceSum <= 0) {
            alert("Šių pamokų apmokėti nereikia (suma lygi 0).");
            return;
        }

        const { paid_from, paid_until } = getPaidFromUntil();
        if (!paid_from || !paid_until) {
            alert("Nepavyko nustatyti pamokų laikotarpio.");
            return;
        }

        isFetchingPaymentLink = true;

        try {
            const data = await requestInvoiceJson(
                `${apiBase}/studentplayground/api/invoice/payment-link/`,
                token,
                {
                method: 'POST',
                body: JSON.stringify({
                    selected_class_ids: selectedClasses,
                    sum: invoiceSum,
                    paid_from: paid_from,
                    paid_until: paid_until
                })
                },
            );
            console.log('🚀 STANDALONE - Full API response:', data);
            console.log('🚀 STANDALONE - Response status:', data.status);
            console.log('🚀 STANDALONE - Payment link:', data.payment_link);
            
            if (data.status === 'success') {
                paymentLink = data.payment_link; // Store for "view again" button
                console.log('🚀 STANDALONE - About to redirect to:', data.payment_link);
                
                // Check if we're in standalone mode (iOS home screen app)
                const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
                console.log('🚀 STANDALONE - Is standalone mode:', isStandalone);
                
                if (isStandalone) {
                    // In standalone mode, use current window redirect
                    console.log('🚀 STANDALONE - Using current window redirect for standalone mode');
                    window.location.href = data.payment_link;
                } else {
                    // Regular browser, try to open new tab
                    const opened = window.open(data.payment_link, '_blank');
                    if (opened) {
                        console.log('🚀 STANDALONE - Window opened successfully');
                    } else {
                        console.error('🚀 STANDALONE - Failed to open window (popup blocker?), using current window');
                        window.location.href = data.payment_link;
                    }
                }
            } else {
                console.error('🚀 STANDALONE - API returned non-success status:', data);
                alert("Nepavyko sugeneruoti mokėjimo nuorodos.");
            }
        } catch (error) {
            if (error instanceof InvoiceApiError && error.authenticationRequired) {
                redirectToInvoiceLogin();
                return;
            }
            if (error instanceof InvoiceApiError && error.code === 'invoice_changed') {
                selectedClasses = [];
                alert("Sąskaitos duomenys pasikeitė. Informacija buvo atnaujinta.");
                await fetchInvoiceData();
                return;
            }
            console.error('🚀 STANDALONE - Error fetching payment link:', error);
            alert("Įvyko klaida generuojant mokėjimo nuorodą.");
        } finally {
            isFetchingPaymentLink = false;
        }
    }




    async function fetchInvoiceData() {
        try {
            invoiceData = await requestInvoiceJson(
                `${apiBase}/studentplayground/api/invoice/`,
                activeToken(),
            );
            const outstandingWindow = buildInvoiceClassWindow(invoiceData.classes || [], {
                visibleOlderMonthCount: Number.MAX_SAFE_INTEGER,
            });
            visibleOlderMonthCount = outstandingWindow.initialOlderMonthCount;
            visibleFutureMonthCount = 0;
            selectedClasses = [];
            errorForInvoiceData = null;
        } catch (error) {
            if (error instanceof InvoiceApiError && error.authenticationRequired) {
                redirectToInvoiceLogin();
                return;
            }
            errorForInvoiceData = error instanceof InvoiceApiError
                ? error.message
                : 'Tinklo klaida. Patikrinkite interneto ryšį.';
        } finally {
            isLoadingInvoiceData = false;
        }
    }

    async function recalculateMyBalance() {
        isRecalculatingMyBalance = true;
        try {
            const result = await requestInvoiceJson(
                `${apiBase}/studentplayground/api/recalculate-my-balance/`,
                activeToken(),
                { method: 'POST' },
            );
            if (result.status === 'success') {
                console.log('✅ USER - Stored balance recalculated:', result.current_balance);
            } else {
                console.warn('⚠️ USER - Balance recalculation failed:', result);
            }
        } catch (e) {
            if (e instanceof InvoiceApiError && e.authenticationRequired) {
                redirectToInvoiceLogin();
                throw e;
            }
            console.warn('⚠️ USER - Balance recalculation request failed:', e);
        } finally {
            isRecalculatingMyBalance = false;
        }
    }

    async function checkIfUserIsTeacher() {
        try {
            const data = await requestInvoiceJson(
                `${apiBase}/studentplayground/isUserTeacher/`,
                activeToken(),
            );
            isUserTeacher = data.isUserTeacher;
        } catch (error) {
            if (error instanceof InvoiceApiError && error.authenticationRequired) {
                redirectToInvoiceLogin();
                throw error;
            }
            console.error('Error checking user type:', error);
            errorForInvoiceData = error instanceof InvoiceApiError
                ? error.message
                : 'Tinklo klaida. Patikrinkite interneto ryšį.';
        } finally {
            isCheckingUserType = false;
        }
    }

    onMount(async () => {
        try {
            await checkIfUserIsTeacher();
        } catch (_error) {
            isLoadingInvoiceData = false;
            return;
        }
        // Only fetch invoice data if user is not a teacher
        if (isUserTeacher === false) {
            // Keep stored StudentBalance.current_balance fresh on every invoice-page load
            try {
                await recalculateMyBalance();
                await fetchInvoiceData();
            } catch (_error) {
                isLoadingInvoiceData = false;
            }
        } else {
            isLoadingInvoiceData = false;
        }
    });
</script>

<style>
    button span {
         font-weight: bolder;
         font-size:larger;
     }
  
     button {
        margin-top:10px;
         box-sizing: border-box;
         display: flex;
         flex-direction: row;
         justify-content: center;
         align-items: center;
         padding: 12px 20px;
         gap: 10px;
     
         width: 100%;
         height: 60px;
     
         /* Purple background */
         background-color: #fab45a;
     
         /* Black border */
         border: 2px solid #000000;
     
         /* Box shadow */
         box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
     
         /* Rounded corners */
         border-radius: 8px;
     
         /* Inside auto layout */
         flex: none;
         order: 0;
         align-self: stretch;
         flex-grow: 0;
     
         /* Remove default button styling */
         outline: none;
         cursor: pointer;
   
         /* Remove the default button background on focus */
         background-image: none;
         transition: background-color 0.3s ease, color 0.3s ease;
     }
  
     /* Button hover effect */
     button:hover {
         background-color: #14695b;
         color:white;
     }
  
     /* Text inside the button */
     span {
        margin-top:20px;
        margin-bottom: 20px;
        font-size: 1rem;
        font-weight: bold;
     
     }
   form {
       padding-bottom: 10px;
       padding-top:15px;
   }
   span {
       font-weight: bold;
   }
   
   label {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 5px;
          display: block;
          color:#14695b;
          text-align: center;
      }

      .balance-info {
          margin: 1rem 0 1.5rem 0;
          padding: 1rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #14695b;
          text-align: center;
      }

      .balance-info > div {
          margin-bottom: 8px;
      }

      .balance-label {
          font-weight: 600;
          color: #14695b;
          display: inline;
      }

      .balance-amount {
          font-weight: 700;
          font-size: 1.1em;
          color: #333;
          margin-left: 8px;
      }

      .balance-date {
          color: #666;
          font-size: 0.9em;
          margin-left: 8px;
      }

      .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          margin: 2rem 0;
      }

      .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #14695b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
      }

      @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
      }

      .loading-text {
          color: #666;
          font-size: 1rem;
          margin: 0;
      }

      .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          margin: 2rem 0;
          background-color: #fff3f3;
          border-radius: 8px;
          border-left: 4px solid #dc3545;
      }

      .error-text {
          color: #dc3545;
          font-size: 1rem;
          margin-bottom: 1rem;
          text-align: center;
      }

      .error-container button {
          background-color: #dc3545;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.9rem;
      }

      .error-container button:hover {
          background-color: #c82333;
      }

      .payments-section {
          margin-top: 2rem;
          padding-top: 2rem;
          border-top: 2px solid #e9ecef;
      }

      .payments-header {
          color: #14695b;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
      }

      .payments-description {
          color: #666;
          font-size: 1rem;
          margin-bottom: 1.5rem;
          line-height: 1.4;
      }

      .no-payments-section {
          margin-top: 2rem;
          padding: 2rem;
          background-color: #f8f9fa;
          border-radius: 8px;
          text-align: center;
      }

      .no-payments-text {
          font-size: 1rem;
          color: #333;
      }

      .class-window-toggle {
          width: auto;
          height: auto;
          min-height: 36px;
          margin: 0;
          padding: 0.45rem 0.9rem;
          align-self: center;
          border: 1px solid #14695b;
          background-color: #fff7ec;
          color: #14695b;
          font-size: 0.9rem;
          font-weight: 600;
      }

      .class-window-controls {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.5rem;
          margin: 0.65rem 0;
      }
  </style>
  
  <NavBarReusable label="Sąskaitos" backLink={backLinkUrl} />
  
  {#if isCheckingUserType}
      <div class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">Kraunamos sąskaitos...</p>
      </div>
  {:else if isUserTeacher}
      <!-- Teacher view - show teacher invoices page -->
      <TeacherInvoicesPage />
  {:else}
      <!-- Student view - show original invoice functionality -->
      {#if isLoadingInvoiceData}
      <div class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">Kraunami sąskaitos duomenys...</p>
      </div>
  {:else if errorForInvoiceData}
      <div class="error-container">
          <p class="error-text">Klaida: {errorForInvoiceData}</p>
          <button on:click={() => { isLoadingInvoiceData = true; errorForInvoiceData = null; fetchInvoiceData(); }}>
              Bandyti dar kartą
          </button>
      </div>
  {:else if invoiceData}
      <div class="balance-info">
      <div>
          <span class="balance-label">Dabartinis balansas:</span>
          <span class="balance-amount">{invoiceData.balance} EUR</span>
      </div>
      <div>
          <span class="balance-label">Paskutinį kartą atnaujinta:</span>
          <span class="balance-date">{new Date(invoiceData.last_updated_date).toLocaleDateString('lt-LT')}</span>
      </div>
      </div>

  <form on:submit|preventDefault={fetchPaymentLinkAndRedirect}>
      <label for="class-select">Pasirinkite Pamokas:</label>
      {#if classWindow.canShowMorePast || classWindow.canShowLessPast}
          <div class="class-window-controls" role="group" aria-label="Ankstesni mėnesiai">
              {#if classWindow.canShowMorePast}
                  <button
                      type="button"
                      class="class-window-toggle"
                      on:click={showOneMorePastMonth}
                      aria-expanded={visibleOlderMonthCount > 0}
                      aria-controls="invoice-class-list"
                  >
                      Rodyti daugiau
                  </button>
              {/if}
              {#if classWindow.canShowLessPast}
                  <button
                      type="button"
                      class="class-window-toggle"
                      on:click={showOneLessPastMonth}
                      aria-expanded="true"
                      aria-controls="invoice-class-list"
                  >
                      Rodyti mažiau
                  </button>
              {/if}
          </div>
      {/if}

      <div id="invoice-class-list">
          <CheckBoxAreForClassesComponent
              classes={classWindow.visibleClasses}
              bind:selectedClasses
          />
      </div>

      {#if classWindow.canShowMoreFuture || classWindow.canShowLessFuture}
          <div class="class-window-controls" role="group" aria-label="Vėlesni mėnesiai">
              {#if classWindow.canShowMoreFuture}
                  <button
                      type="button"
                      class="class-window-toggle"
                      on:click={showOneMoreFutureMonth}
                      aria-expanded={visibleFutureMonthCount > 0}
                      aria-controls="invoice-class-list"
                  >
                      Rodyti daugiau
                  </button>
              {/if}
              {#if classWindow.canShowLessFuture}
                  <button
                      type="button"
                      class="class-window-toggle"
                      on:click={showOneLessFutureMonth}
                      aria-expanded="true"
                      aria-controls="invoice-class-list"
                  >
                      Rodyti mažiau
                  </button>
              {/if}
          </div>
      {/if}

      <p>Viso: {Math.round(invoiceSum)} EUR</p>

      <button type="submit" disabled={isFetchingPaymentLink}>
          <span>{isFetchingPaymentLink ? 'Kuriama nuoroda...' : 'Apmokėti'}</span>
        </button>
  </form>

  {#if invoiceData.previous_payments.length > 0}
      <div class="payments-section">
          <h2 class="payments-header">Atlikti mokėjimai</h2>
          <p class="payments-description">Čia galite peržiūrėti savo mokėjimo istoriją ir atsisiųsti sąskaitas.</p>
          <CompletedPaymentsList payments={invoiceData.previous_payments} />
      </div>
  {:else}
      <div class="no-payments-section">
          <h2 class="payments-header">Atlikti mokėjimai</h2>
          <p class="no-payments-text">Kol kas neturite atliktų mokėjimų.</p>
      </div>
  {/if}

  {/if} <!-- End of student view -->
{/if} <!-- End of user type check -->
